/**
 * Generated art lookup. scripts/art/optimize.mjs writes generated-index.json with the
 * list of WebP files that exist under public/art/generated. Anything missing falls back
 * to the SVG placeholders in ./placeholders.ts, and every <img>/texture load also falls
 * back on error, so the game stays playable with zero generated art.
 */
import index from './generated-index.json';

type Index = {
  backgrounds?: string[];
  depth?: string[];
  narrator?: string[];
  cards?: string[];
  overlays?: string[];
  title?: string[];
  moodAliases?: Record<string, string>;
  layers?: Record<string, LayerMeta>;
  props?: Record<string, number>;
};

export interface SceneLight {
  x: number;
  y: number;
  layer: number;
  size: number;
}

export interface LayerMeta {
  count: number;
  moods: string[];
  lights: SceneLight[];
}

export interface LayerSet {
  /** back to front; index 0 is the painted-out backdrop */
  urls: string[];
  /** two-layer fallback: backdrop + everything in front merged */
  twoLayer: string[];
  lights: SceneLight[];
}

const idx = index as Index;
const BASE = `${import.meta.env.BASE_URL}art/generated`;
const has = (list: string[] | undefined, id: string) => !!list?.includes(id);

export function resolveMood(mood: string): string {
  return idx.moodAliases?.[mood] ?? mood;
}

export function backgroundUrl(sceneId: string, mood: string): string | null {
  const m = resolveMood(mood);
  if (has(idx.backgrounds, `${sceneId}_${m}`)) return `${BASE}/backgrounds/${sceneId}_${m}.webp`;
  if (has(idx.backgrounds, `${sceneId}_calm`)) return `${BASE}/backgrounds/${sceneId}_calm.webp`;
  return null;
}

export function depthUrl(sceneId: string): string | null {
  return has(idx.depth, sceneId) ? `${BASE}/depth/${sceneId}.webp` : null;
}

const EXPRESSION_ALIASES: Record<string, string> = { gritting: 'gritting', annoyed: 'annoyed' };

export function narratorUrl(expression: string | undefined): string | null {
  const e = EXPRESSION_ALIASES[expression ?? ''] ?? expression ?? 'neutral';
  if (has(idx.narrator, e)) return `${BASE}/narrator/${e}.webp`;
  if (has(idx.narrator, 'neutral')) return `${BASE}/narrator/neutral.webp`;
  return null;
}

export function cardArtUrl(cardId: string): string | null {
  if (has(idx.cards, cardId)) return `${BASE}/cards/${cardId}.webp`;
  const base = cardId.replace(/_foil$/, '');
  if (has(idx.cards, base)) return `${BASE}/cards/${base}.webp`;
  return null;
}

export function overlayUrl(overlayId: string): string | null {
  return has(idx.overlays, overlayId) ? `${BASE}/overlays/${overlayId}.webp` : null;
}

export function titleArtUrl(): string | null {
  return has(idx.title, 'title_keyart') ? `${BASE}/title/title_keyart.webp` : null;
}

export function layerSet(sceneId: string, mood: string): LayerSet | null {
  const meta = idx.layers?.[sceneId];
  if (!meta) return null;
  const want = resolveMood(mood);
  const m = meta.moods.includes(want) ? want : meta.moods.includes('calm') ? 'calm' : meta.moods[0];
  if (!m) return null;
  const urls = Array.from({ length: meta.count }, (_, i) => `${BASE}/layers/${sceneId}_${m}_l${i}.webp`);
  return { urls, twoLayer: [urls[0], `${BASE}/layers/${sceneId}_${m}_front.webp`], lights: meta.lights ?? [] };
}

export function propArt(id: string): { url: string; aspect: number } | null {
  const aspect = idx.props?.[id];
  return aspect ? { url: `${BASE}/props/${id}.webp`, aspect } : null;
}
